import React, { useCallback, useEffect, useState } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { api } from '../../src/api/client';
import { useTheme } from '../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import Input from '../../src/components/Input';
import Button from '../../src/components/Button';
import Card from '../../src/components/Card';
import ContentContainer from '../../src/components/ContentContainer';

interface CmsSubsection {
  title: string;
  content: string[];
}

interface CmsSection {
  icon?: string;
  title: string;
  content: string[];
  subsections?: CmsSubsection[];
}

interface CmsPageSummary {
  slug: string;
  title: string;
  updatedAt: string;
}

interface CmsPage {
  slug: string;
  title: string;
  version: string | null;
  lastUpdated: string | null;
  sections: CmsSection[];
  updatedAt: string;
}

declare const alert: (msg: string) => void;

export default function AdminCmsScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);

  const [pages, setPages] = useState<CmsPageSummary[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [editing, setEditing] = useState<CmsPage | null>(null);
  const [loadingPage, setLoadingPage] = useState(false);
  const [saving, setSaving] = useState(false);
  // Raw JSON editing mode for the sections array.  Open it as a
  // collapsible textarea below the structured editor; edits in either
  // view stay in sync via parse-on-change.
  const [rawJsonOpen, setRawJsonOpen] = useState(false);
  const [rawJsonText, setRawJsonText] = useState('');
  const [rawJsonError, setRawJsonError] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    try {
      setLoadingList(true);
      const data = await api<CmsPageSummary[]>('/api/admin/cms/pages');
      setPages(data);
    } catch (err: any) {
      alert(err?.message || 'Failed to load pages');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openPage = async (slug: string) => {
    try {
      setLoadingPage(true);
      setEditingSlug(slug);
      const page = await api<CmsPage>(`/api/admin/cms/pages/${slug}`);
      const safeSections = Array.isArray(page.sections) ? page.sections : [];
      setEditing({ ...page, sections: safeSections });
      setRawJsonText(JSON.stringify(safeSections, null, 2));
      setRawJsonError(null);
      setRawJsonOpen(false);
    } catch (err: any) {
      alert(err?.message || 'Failed to load page');
      setEditingSlug(null);
    } finally {
      setLoadingPage(false);
    }
  };

  const closeEditor = () => {
    setEditingSlug(null);
    setEditing(null);
    setRawJsonText('');
    setRawJsonError(null);
    setRawJsonOpen(false);
  };

  // Update both editing.sections AND the raw JSON text in lockstep so
  // toggling between structured/raw views always shows the latest state.
  const setSections = (next: CmsSection[]) => {
    if (!editing) return;
    setEditing({ ...editing, sections: next });
    setRawJsonText(JSON.stringify(next, null, 2));
    setRawJsonError(null);
  };

  const onRawJsonChange = (text: string) => {
    setRawJsonText(text);
    try {
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) throw new Error('Top-level value must be an array of sections');
      setRawJsonError(null);
      if (editing) setEditing({ ...editing, sections: parsed });
    } catch (e: any) {
      setRawJsonError(e.message || 'Invalid JSON');
    }
  };

  const save = async () => {
    if (!editing) return;
    if (rawJsonError) {
      alert('Fix the JSON parse error before saving.');
      return;
    }
    try {
      setSaving(true);
      await api(`/api/admin/cms/pages/${editing.slug}`, {
        method: 'PUT',
        body: {
          title: editing.title,
          version: editing.version,
          lastUpdated: editing.lastUpdated,
          sections: editing.sections,
        },
      });
      alert('Saved');
      await fetchList();
    } catch (err: any) {
      alert(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Section editing helpers ──────────────────────────────────────
  const updateSection = (idx: number, patch: Partial<CmsSection>) => {
    if (!editing) return;
    const next = [...editing.sections];
    next[idx] = { ...next[idx], ...patch };
    setSections(next);
  };
  const addSection = () => {
    if (!editing) return;
    setSections([...editing.sections, { title: 'New section', content: [''] }]);
  };
  const removeSection = (idx: number) => {
    if (!editing) return;
    setSections(editing.sections.filter((_, i) => i !== idx));
  };
  const moveSection = (idx: number, dir: -1 | 1) => {
    if (!editing) return;
    const next = [...editing.sections];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setSections(next);
  };
  const addParagraph = (sIdx: number) => {
    if (!editing) return;
    const next = [...editing.sections];
    next[sIdx] = { ...next[sIdx], content: [...next[sIdx].content, ''] };
    setSections(next);
  };
  const updateParagraph = (sIdx: number, pIdx: number, value: string) => {
    if (!editing) return;
    const next = [...editing.sections];
    const content = [...next[sIdx].content];
    content[pIdx] = value;
    next[sIdx] = { ...next[sIdx], content };
    setSections(next);
  };
  const removeParagraph = (sIdx: number, pIdx: number) => {
    if (!editing) return;
    const next = [...editing.sections];
    next[sIdx] = { ...next[sIdx], content: next[sIdx].content.filter((_, i) => i !== pIdx) };
    setSections(next);
  };

  // ── Subsection helpers (one level deep) ──────────────────────────
  const addSubsection = (sIdx: number) => {
    if (!editing) return;
    const next = [...editing.sections];
    const subs = [...(next[sIdx].subsections || []), { title: 'New subsection', content: [''] }];
    next[sIdx] = { ...next[sIdx], subsections: subs };
    setSections(next);
  };
  const updateSubsection = (sIdx: number, subIdx: number, patch: Partial<CmsSubsection>) => {
    if (!editing) return;
    const next = [...editing.sections];
    const subs = [...(next[sIdx].subsections || [])];
    subs[subIdx] = { ...subs[subIdx], ...patch };
    next[sIdx] = { ...next[sIdx], subsections: subs };
    setSections(next);
  };
  const removeSubsection = (sIdx: number, subIdx: number) => {
    if (!editing) return;
    const next = [...editing.sections];
    const subs = (next[sIdx].subsections || []).filter((_, i) => i !== subIdx);
    next[sIdx] = { ...next[sIdx], subsections: subs.length ? subs : undefined };
    setSections(next);
  };
  const moveSubsection = (sIdx: number, subIdx: number, dir: -1 | 1) => {
    if (!editing) return;
    const next = [...editing.sections];
    const subs = [...(next[sIdx].subsections || [])];
    const target = subIdx + dir;
    if (target < 0 || target >= subs.length) return;
    [subs[subIdx], subs[target]] = [subs[target], subs[subIdx]];
    next[sIdx] = { ...next[sIdx], subsections: subs };
    setSections(next);
  };
  const addSubParagraph = (sIdx: number, subIdx: number) => {
    if (!editing) return;
    const next = [...editing.sections];
    const subs = [...(next[sIdx].subsections || [])];
    subs[subIdx] = { ...subs[subIdx], content: [...subs[subIdx].content, ''] };
    next[sIdx] = { ...next[sIdx], subsections: subs };
    setSections(next);
  };
  const updateSubParagraph = (sIdx: number, subIdx: number, pIdx: number, value: string) => {
    if (!editing) return;
    const next = [...editing.sections];
    const subs = [...(next[sIdx].subsections || [])];
    const content = [...subs[subIdx].content];
    content[pIdx] = value;
    subs[subIdx] = { ...subs[subIdx], content };
    next[sIdx] = { ...next[sIdx], subsections: subs };
    setSections(next);
  };
  const removeSubParagraph = (sIdx: number, subIdx: number, pIdx: number) => {
    if (!editing) return;
    const next = [...editing.sections];
    const subs = [...(next[sIdx].subsections || [])];
    subs[subIdx] = { ...subs[subIdx], content: subs[subIdx].content.filter((_, i) => i !== pIdx) };
    next[sIdx] = { ...next[sIdx], subsections: subs };
    setSections(next);
  };

  // ── List view ────────────────────────────────────────────────────
  if (!editingSlug) {
    return (
      <View style={styles.container}>
        <ContentContainer>
          <Text style={styles.title}>Content Pages</Text>
          {loadingList ? (
            <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
          ) : (
            <View>
              {pages.map((p) => (
                <TouchableOpacity key={p.slug} onPress={() => openPage(p.slug)}>
                  <Card style={{ marginBottom: spacing.sm }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.pageTitle}>{p.title}</Text>
                        <Text style={styles.pageSlug}>{p.slug}</Text>
                        <Text style={styles.pageMeta}>Updated {new Date(p.updatedAt).toLocaleString()}</Text>
                      </View>
                      <Ionicons name="chevron-forward" size={20} color={colors.textLight} />
                    </View>
                  </Card>
                </TouchableOpacity>
              ))}
              {pages.length === 0 && (
                <Text style={styles.empty}>No CMS pages exist yet.</Text>
              )}
            </View>
          )}
        </ContentContainer>
      </View>
    );
  }

  // ── Editor view ──────────────────────────────────────────────────
  if (loadingPage || !editing) {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xxl }} />
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={{ paddingBottom: spacing.xxl }}>
      <ContentContainer>
        <View style={styles.editorHeader}>
          <TouchableOpacity onPress={closeEditor} style={styles.backBtn}>
            <Ionicons name="chevron-back" size={22} color={colors.primary} />
            <Text style={styles.backText}>Back</Text>
          </TouchableOpacity>
          <Button title="Save" onPress={save} loading={saving} size="sm" />
        </View>

        <Text style={styles.editingSlug}>{editing.slug}</Text>

        <Input
          label="Title"
          value={editing.title}
          onChangeText={(v) => setEditing({ ...editing, title: v })}
        />
        <Input
          label="Version (optional)"
          value={editing.version || ''}
          onChangeText={(v) => setEditing({ ...editing, version: v || null })}
          placeholder="e.g. 1.2"
        />
        <Input
          label="Last updated (optional)"
          value={editing.lastUpdated || ''}
          onChangeText={(v) => setEditing({ ...editing, lastUpdated: v || null })}
          placeholder="e.g. 2026-04-08"
        />

        <Text style={styles.sectionsHeader}>Sections ({editing.sections.length})</Text>

        {editing.sections.map((section, sIdx) => (
          <Card key={sIdx} style={{ marginBottom: spacing.md }}>
            <View style={styles.sectionToolbar}>
              <Text style={styles.sectionLabel}>Section {sIdx + 1}</Text>
              <View style={{ flexDirection: 'row', gap: spacing.xs }}>
                <TouchableOpacity onPress={() => moveSection(sIdx, -1)} style={styles.iconBtn}>
                  <Ionicons name="arrow-up" size={18} color={colors.primary} />
                </TouchableOpacity>
                <TouchableOpacity onPress={() => moveSection(sIdx, 1)} style={styles.iconBtn}>
                  <Ionicons name="arrow-down" size={18} color={colors.primary} />
                </TouchableOpacity>
                <TouchableOpacity onPress={() => removeSection(sIdx)} style={styles.iconBtn}>
                  <Ionicons name="trash" size={18} color={colors.danger} />
                </TouchableOpacity>
              </View>
            </View>
            <Input
              label="Icon (optional Ionicons name, e.g. cube-outline)"
              value={section.icon || ''}
              onChangeText={(v) => updateSection(sIdx, { icon: v || undefined })}
              placeholder="cube-outline"
            />
            <Input
              label="Section title"
              value={section.title}
              onChangeText={(v) => updateSection(sIdx, { title: v })}
            />
            <Text style={styles.paragraphsLabel}>Paragraphs ({section.content.length})</Text>
            {section.content.map((paragraph, pIdx) => (
              <View key={pIdx} style={{ marginBottom: spacing.sm }}>
                <Input
                  value={paragraph}
                  onChangeText={(v) => updateParagraph(sIdx, pIdx, v)}
                  multiline
                  numberOfLines={4}
                  style={{ minHeight: 80, textAlignVertical: 'top' }}
                />
                <TouchableOpacity onPress={() => removeParagraph(sIdx, pIdx)} style={styles.removeParaBtn}>
                  <Ionicons name="close" size={16} color={colors.danger} />
                  <Text style={styles.removeParaText}>Remove paragraph</Text>
                </TouchableOpacity>
              </View>
            ))}
            <Button
              title="Add paragraph"
              onPress={() => addParagraph(sIdx)}
              variant="ghost"
              size="sm"
              icon={<Ionicons name="add" size={16} color={colors.primary} />}
            />

            {/* ── Subsections (one level deep) ── */}
            {section.subsections && section.subsections.length > 0 && (
              <Text style={styles.subsectionsHeader}>Subsections ({section.subsections.length})</Text>
            )}
            {section.subsections?.map((sub, subIdx) => (
              <View key={subIdx} style={styles.subsectionCard}>
                <View style={styles.sectionToolbar}>
                  <Text style={styles.sectionLabel}>Subsection {subIdx + 1}</Text>
                  <View style={{ flexDirection: 'row', gap: spacing.xs }}>
                    <TouchableOpacity onPress={() => moveSubsection(sIdx, subIdx, -1)} style={styles.iconBtn}>
                      <Ionicons name="arrow-up" size={16} color={colors.primary} />
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => moveSubsection(sIdx, subIdx, 1)} style={styles.iconBtn}>
                      <Ionicons name="arrow-down" size={16} color={colors.primary} />
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => removeSubsection(sIdx, subIdx)} style={styles.iconBtn}>
                      <Ionicons name="trash" size={16} color={colors.danger} />
                    </TouchableOpacity>
                  </View>
                </View>
                <Input
                  label="Subsection title"
                  value={sub.title}
                  onChangeText={(v) => updateSubsection(sIdx, subIdx, { title: v })}
                />
                <Text style={styles.paragraphsLabel}>Paragraphs ({sub.content.length})</Text>
                {sub.content.map((paragraph, pIdx) => (
                  <View key={pIdx} style={{ marginBottom: spacing.sm }}>
                    <Input
                      value={paragraph}
                      onChangeText={(v) => updateSubParagraph(sIdx, subIdx, pIdx, v)}
                      multiline
                      numberOfLines={3}
                      style={{ minHeight: 60, textAlignVertical: 'top' }}
                    />
                    <TouchableOpacity onPress={() => removeSubParagraph(sIdx, subIdx, pIdx)} style={styles.removeParaBtn}>
                      <Ionicons name="close" size={14} color={colors.danger} />
                      <Text style={styles.removeParaText}>Remove paragraph</Text>
                    </TouchableOpacity>
                  </View>
                ))}
                <Button
                  title="Add paragraph"
                  onPress={() => addSubParagraph(sIdx, subIdx)}
                  variant="ghost"
                  size="sm"
                  icon={<Ionicons name="add" size={14} color={colors.primary} />}
                />
              </View>
            ))}
            <Button
              title="Add subsection"
              onPress={() => addSubsection(sIdx)}
              variant="ghost"
              size="sm"
              icon={<Ionicons name="add" size={14} color={colors.primary} />}
              style={{ marginTop: spacing.sm }}
            />
          </Card>
        ))}

        <Button
          title="Add section"
          onPress={addSection}
          variant="outline"
          icon={<Ionicons name="add" size={18} color={colors.primary} />}
          style={{ marginTop: spacing.md }}
        />

        {/* ── Raw JSON editor (collapsible) ── */}
        <TouchableOpacity
          onPress={() => setRawJsonOpen((v) => !v)}
          style={styles.rawHeader}
          accessibilityRole="button"
        >
          <Ionicons
            name={rawJsonOpen ? 'chevron-down' : 'chevron-forward'}
            size={18}
            color={colors.text}
          />
          <Text style={styles.rawHeaderText}>Edit raw JSON</Text>
          {rawJsonError && <Text style={styles.rawErrorBadge}>parse error</Text>}
        </TouchableOpacity>
        {rawJsonOpen && (
          <View style={styles.rawBox}>
            {rawJsonError && (
              <Text style={styles.rawError}>{rawJsonError}</Text>
            )}
            <Input
              value={rawJsonText}
              onChangeText={onRawJsonChange}
              multiline
              numberOfLines={20}
              style={styles.rawTextarea}
              autoCapitalize="none"
              autoCorrect={false}
            />
            <Text style={styles.rawHelp}>
              Edit the sections array as JSON. Changes sync to the structured editor in real time.
              Save is disabled while there is a parse error.
            </Text>
          </View>
        )}

        <Button
          title="Save"
          onPress={save}
          loading={saving}
          disabled={!!rawJsonError}
          style={{ marginTop: spacing.md }}
        />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  title: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  pageTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  pageSlug: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2, fontFamily: 'monospace' },
  pageMeta: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 4 },
  empty: { fontSize: fontSize.sm, color: c.textSecondary, textAlign: 'center', paddingVertical: spacing.xl },
  editorHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.md },
  backBtn: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  backText: { color: c.primary, fontWeight: fontWeight.semibold, fontSize: fontSize.md },
  editingSlug: { fontFamily: 'monospace', color: c.textLight, fontSize: fontSize.xs, marginBottom: spacing.md },
  sectionsHeader: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginTop: spacing.lg, marginBottom: spacing.sm },
  sectionToolbar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.sm },
  sectionLabel: { fontSize: fontSize.sm, color: c.textSecondary, fontWeight: fontWeight.semibold },
  iconBtn: { padding: spacing.xs },
  paragraphsLabel: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: spacing.sm, marginBottom: spacing.xs },
  removeParaBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 4, alignSelf: 'flex-start' },
  removeParaText: { fontSize: fontSize.xs, color: c.danger },
  subsectionsHeader: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.textSecondary, marginTop: spacing.md, marginBottom: spacing.xs },
  subsectionCard: {
    marginTop: spacing.sm,
    marginLeft: spacing.md,
    paddingLeft: spacing.md,
    borderLeftWidth: 2,
    borderLeftColor: c.borderLight,
  },
  rawHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: spacing.lg, paddingVertical: spacing.sm },
  rawHeaderText: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  rawErrorBadge: { fontSize: fontSize.xs, color: c.danger, marginLeft: spacing.sm },
  rawBox: { backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.md, padding: spacing.sm, marginBottom: spacing.md },
  rawError: { fontSize: fontSize.xs, color: c.danger, marginBottom: spacing.sm },
  rawTextarea: { minHeight: 320, fontFamily: 'monospace', fontSize: fontSize.xs, textAlignVertical: 'top' },
  rawHelp: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: spacing.xs },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
